'use client';

/**
 * Foundry Context
 * React context for managing business plan state
 *
 * Supports async AI operations with polling:
 * - AI endpoints return immediately with 'processing' status
 * - Context enables polling to detect when operations complete
 * - UI reflects real-time status from section.status and user_context flags
 */

import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  useEffect,
  type ReactNode,
} from 'react';
import type {
  BusinessPlan,
  BusinessPlanWithSections,
  PlanSection,
  LeanCanvas,
  UserContext,
  SectionKey,
  SanityCheckResult,
  CreatePlanRequest,
} from './types';
import * as api from './api';
import {
  usePlanPolling,
  hasProcessingSections,
  isCanvasGenerating,
} from './usePlanPolling';

// ============================================================================
// State Types
// ============================================================================

interface FoundryState {
  // Plans list
  plans: BusinessPlan[];
  plansLoading: boolean;

  // Current plan
  currentPlan: BusinessPlanWithSections | null;
  currentPlanLoading: boolean;

  // AI Operations
  aiLoading: boolean;
  aiError: string | null;

  // Sanity Check
  sanityCheckResult: SanityCheckResult | null;

  // UI State
  error: string | null;
}

type FoundryAction =
  | { type: 'SET_PLANS'; plans: BusinessPlan[] }
  | { type: 'SET_PLANS_LOADING'; loading: boolean }
  | { type: 'SET_CURRENT_PLAN'; plan: BusinessPlanWithSections | null }
  | { type: 'SET_CURRENT_PLAN_LOADING'; loading: boolean }
  | { type: 'UPDATE_CURRENT_PLAN'; updates: Partial<BusinessPlan> }
  | { type: 'UPDATE_CANVAS'; canvas: Partial<LeanCanvas> }
  | { type: 'UPDATE_SECTION'; section: PlanSection }
  | { type: 'SET_AI_LOADING'; loading: boolean }
  | { type: 'SET_AI_ERROR'; error: string | null }
  | { type: 'SET_SANITY_CHECK_RESULT'; result: SanityCheckResult | null }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'ADD_PLAN'; plan: BusinessPlan }
  | { type: 'REMOVE_PLAN'; planId: string };

const initialState: FoundryState = {
  plans: [],
  plansLoading: false,
  currentPlan: null,
  currentPlanLoading: false,
  aiLoading: false,
  aiError: null,
  sanityCheckResult: null,
  error: null,
};

function foundryReducer(state: FoundryState, action: FoundryAction): FoundryState {
  switch (action.type) {
    case 'SET_PLANS':
      return { ...state, plans: action.plans, error: null };
    case 'SET_PLANS_LOADING':
      return { ...state, plansLoading: action.loading };
    case 'SET_CURRENT_PLAN':
      return { ...state, currentPlan: action.plan, error: null };
    case 'SET_CURRENT_PLAN_LOADING':
      return { ...state, currentPlanLoading: action.loading };
    case 'UPDATE_CURRENT_PLAN':
      if (!state.currentPlan) return state;
      return {
        ...state,
        currentPlan: { ...state.currentPlan, ...action.updates },
      };
    case 'UPDATE_CANVAS':
      if (!state.currentPlan) return state;
      return {
        ...state,
        currentPlan: {
          ...state.currentPlan,
          lean_canvas: { ...state.currentPlan.lean_canvas, ...action.canvas },
        },
      };
    case 'UPDATE_SECTION':
      if (!state.currentPlan) return state;
      const sections = state.currentPlan.sections.map((s) =>
        s.section_key === action.section.section_key ? action.section : s
      );
      // Add section if it doesn't exist
      if (!sections.find((s) => s.section_key === action.section.section_key)) {
        sections.push(action.section);
      }
      return {
        ...state,
        currentPlan: { ...state.currentPlan, sections },
      };
    case 'SET_AI_LOADING':
      return { ...state, aiLoading: action.loading, aiError: null };
    case 'SET_AI_ERROR':
      return { ...state, aiError: action.error, aiLoading: false };
    case 'SET_SANITY_CHECK_RESULT':
      return { ...state, sanityCheckResult: action.result };
    case 'SET_ERROR':
      return { ...state, error: action.error };
    case 'ADD_PLAN':
      return { ...state, plans: [action.plan, ...state.plans] };
    case 'REMOVE_PLAN':
      return {
        ...state,
        plans: state.plans.filter((p) => p.id !== action.planId),
      };
    default:
      return state;
  }
}

// ============================================================================
// Context
// ============================================================================

interface FoundryContextValue {
  state: FoundryState;
  actions: {
    // Plans
    loadPlans: () => Promise<void>;
    loadPlan: (planId: string) => Promise<void>;
    createPlan: (data: CreatePlanRequest) => Promise<BusinessPlan>;
    updatePlan: (updates: Partial<BusinessPlan>) => Promise<void>;
    deletePlan: (planId: string) => Promise<void>;

    // Canvas
    generateCanvas: (businessIdea: string, userContext: Partial<UserContext>) => Promise<void>;
    updateCanvas: (updates: Partial<LeanCanvas>) => void;

    // Sections
    expandSection: (sectionKey: SectionKey, additionalInstructions?: string) => Promise<void>;

    // Sanity Check
    runSanityCheck: () => Promise<void>;

    // Export
    downloadMarkdown: () => Promise<void>;

    // Error handling
    clearError: () => void;
  };
}

const FoundryContext = createContext<FoundryContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

export function FoundryProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(foundryReducer, initialState);

  // Load all plans
  const loadPlans = useCallback(async () => {
    dispatch({ type: 'SET_PLANS_LOADING', loading: true });
    try {
      const plans = await api.listPlans();
      dispatch({ type: 'SET_PLANS', plans });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', error: (error as Error).message });
    } finally {
      dispatch({ type: 'SET_PLANS_LOADING', loading: false });
    }
  }, []);

  // Load a specific plan
  const loadPlan = useCallback(async (planId: string) => {
    dispatch({ type: 'SET_CURRENT_PLAN_LOADING', loading: true });
    try {
      const plan = await api.getPlan(planId);
      dispatch({ type: 'SET_CURRENT_PLAN', plan });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', error: (error as Error).message });
    } finally {
      dispatch({ type: 'SET_CURRENT_PLAN_LOADING', loading: false });
    }
  }, []);

  // Create a new plan
  const createPlan = useCallback(async (data: CreatePlanRequest) => {
    const plan = await api.createPlan(data);
    dispatch({ type: 'ADD_PLAN', plan });
    return plan;
  }, []);

  // Update current plan
  const updatePlan = useCallback(async (updates: Partial<BusinessPlan>) => {
    if (!state.currentPlan) return;
    try {
      // Convert null values to undefined for API compatibility
      const apiUpdates: Parameters<typeof api.updatePlan>[1] = {
        name: updates.name ?? undefined,
        description: updates.description ?? undefined,
        industry: updates.industry ?? undefined,
        business_idea: updates.business_idea ?? undefined,
        status: updates.status,
        lean_canvas: updates.lean_canvas ?? undefined,
        user_context: updates.user_context ?? undefined,
      };
      const plan = await api.updatePlan(state.currentPlan.id, apiUpdates);
      dispatch({ type: 'UPDATE_CURRENT_PLAN', updates: plan });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', error: (error as Error).message });
    }
  }, [state.currentPlan]);

  // Delete a plan
  const deletePlan = useCallback(async (planId: string) => {
    await api.deletePlan(planId);
    dispatch({ type: 'REMOVE_PLAN', planId });
    if (state.currentPlan?.id === planId) {
      dispatch({ type: 'SET_CURRENT_PLAN', plan: null });
    }
  }, [state.currentPlan]);

  // Generate canvas (async - returns immediately, polling updates state)
  const generateCanvas = useCallback(
    async (businessIdea: string, userContext: Partial<UserContext>) => {
      if (!state.currentPlan) return;
      try {
        // Optimistically set generating flag in local state
        dispatch({
          type: 'UPDATE_CURRENT_PLAN',
          updates: {
            user_context: {
              ...state.currentPlan.user_context,
              _canvas_generating: true,
              _canvas_error: null,
            } as UserContext,
          },
        });

        // API returns immediately - actual generation happens in background
        await api.generateCanvas(state.currentPlan.id, {
          business_idea: businessIdea,
          user_context: userContext,
        });
        // Polling will pick up the result when ready
      } catch (error) {
        dispatch({ type: 'SET_AI_ERROR', error: (error as Error).message });
        // Clear the generating flag on error
        dispatch({
          type: 'UPDATE_CURRENT_PLAN',
          updates: {
            user_context: {
              ...state.currentPlan.user_context,
              _canvas_generating: false,
            } as UserContext,
          },
        });
      }
    },
    [state.currentPlan]
  );

  // Update canvas locally
  const updateCanvas = useCallback((updates: Partial<LeanCanvas>) => {
    dispatch({ type: 'UPDATE_CANVAS', canvas: updates });
  }, []);

  // Expand section (async - returns immediately with 'processing' status, polling updates state)
  const expandSection = useCallback(
    async (sectionKey: SectionKey, additionalInstructions?: string) => {
      if (!state.currentPlan) return;
      try {
        // API sets section to 'processing' status and returns immediately
        const result = await api.expandSection(state.currentPlan.id, {
          section_key: sectionKey,
          additional_instructions: additionalInstructions,
        });

        // Optimistically update local state with the processing section
        dispatch({ type: 'UPDATE_SECTION', section: result.section });
        // Polling will pick up the completed/failed result when ready
      } catch (error) {
        dispatch({ type: 'SET_AI_ERROR', error: (error as Error).message });
      }
    },
    [state.currentPlan]
  );

  // Run sanity check
  const runSanityCheck = useCallback(async () => {
    if (!state.currentPlan) return;
    dispatch({ type: 'SET_AI_LOADING', loading: true });
    try {
      const result = await api.runSanityCheck(state.currentPlan.id);
      dispatch({ type: 'SET_SANITY_CHECK_RESULT', result });
    } catch (error) {
      dispatch({ type: 'SET_AI_ERROR', error: (error as Error).message });
    } finally {
      dispatch({ type: 'SET_AI_LOADING', loading: false });
    }
  }, [state.currentPlan]);

  // Download markdown
  const downloadMarkdown = useCallback(async () => {
    if (!state.currentPlan) return;
    try {
      await api.downloadMarkdown(state.currentPlan.id);
    } catch (error) {
      dispatch({ type: 'SET_ERROR', error: (error as Error).message });
    }
  }, [state.currentPlan]);

  // Clear error
  const clearError = useCallback(() => {
    dispatch({ type: 'SET_ERROR', error: null });
    dispatch({ type: 'SET_AI_ERROR', error: null });
  }, []);

  // ============================================================================
  // Polling for Async AI Operations
  // ============================================================================

  // Check if any async operations are in progress
  const hasActiveOperations =
    hasProcessingSections(state.currentPlan) ||
    isCanvasGenerating(state.currentPlan);

  // Handle plan updates from polling
  const handlePlanUpdate = useCallback((updatedPlan: BusinessPlanWithSections) => {
    // Only update if there are meaningful changes
    dispatch({ type: 'SET_CURRENT_PLAN', plan: updatedPlan });
  }, []);

  // Enable polling when async operations are in progress
  usePlanPolling(state.currentPlan?.id ?? null, {
    enabled: hasActiveOperations,
    pollInterval: 2000,
    onUpdate: handlePlanUpdate,
  });

  // Memoize actions to prevent unnecessary re-renders and infinite loops
  const actions = useMemo(
    () => ({
      loadPlans,
      loadPlan,
      createPlan,
      updatePlan,
      deletePlan,
      generateCanvas,
      updateCanvas,
      expandSection,
      runSanityCheck,
      downloadMarkdown,
      clearError,
    }),
    [
      loadPlans,
      loadPlan,
      createPlan,
      updatePlan,
      deletePlan,
      generateCanvas,
      updateCanvas,
      expandSection,
      runSanityCheck,
      downloadMarkdown,
      clearError,
    ]
  );

  // Memoize the context value
  const value = useMemo<FoundryContextValue>(
    () => ({ state, actions }),
    [state, actions]
  );

  return (
    <FoundryContext.Provider value={value}>{children}</FoundryContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

export function useFoundry() {
  const context = useContext(FoundryContext);
  if (!context) {
    throw new Error('useFoundry must be used within a FoundryProvider');
  }
  return context;
}
